/**
 * Switches the product page between mattress sizes without a full page reload,
 * so clicking "King" on the Single page updates the whole page in place.
 *
 * Each size is its own Shopify product rather than a variant of one, so there
 * is no native variant picker to hook into.
 *
 * Why the WHOLE page body is swapped, not just <product-info>
 * ----------------------------------------------------------
 * The first version of this file re-rendered only `<product-info>` through the
 * Section Rendering API. That covers the title, price, gallery, buy buttons and
 * size pills, because those all live inside that one element — but everything
 * further down the page is a SEPARATE SECTION in the template: Product tabs,
 * the FAQ, the "Why the True Rest ..." banner. Those were left untouched, so
 * switching from Single to King gave you the King's price above a Technical
 * Information tab still reading "Size: Single, 190 x 90 x 25cm". The client
 * reported exactly that on 2026-08-24.
 *
 * So this fetches the target product's page and replaces every section inside
 * `#MainContent` with its counterpart. Sections are matched by the part of the
 * id after `__` (`...template--123__faq` -> `faq`) rather than by the id
 * itself, because each mattress has its own template and therefore its own
 * numeric template id. Matching that way also means any section added to the
 * product templates later is carried across without touching this file.
 *
 * Any failure falls through to ordinary navigation, so the links keep working
 * without JS or if the fetch fails.
 */
(function () {
  if (window.__trueRestSizeSwitcher) return;
  window.__trueRestSizeSwitcher = true;

  const PILL_SELECTOR = 'a.product__size-pill';
  const ANCHOR_SELECTOR = '.product__other-sizes';
  const MAIN_SELECTOR = '#MainContent';
  const SECTION_SELECTOR = '[id^="shopify-section-"]';

  let inFlight = null;

  const getProductInfo = () => document.querySelector('product-info');

  /**
   * `shopify-section-template--31516663546188__faq` -> `faq`. Null for the
   * section-group ids (header, footer) that carry no `__`, which is what keeps
   * this scoped to template sections.
   */
  function sectionKey(element) {
    const id = element.id || '';
    const separator = id.indexOf('__');
    return separator === -1 ? null : id.slice(separator + 2);
  }

  async function swapTo(url, { push }) {
    const main = document.querySelector(MAIN_SELECTOR);
    const current = getProductInfo();
    if (!main || !current) return false;

    if (inFlight) inFlight.abort();
    const controller = new AbortController();
    inFlight = controller;
    current.classList.add('product-info--switching');

    // Keeps the size selector pinned where it already is on screen. The two
    // products' descriptions differ in length, so without this the page jumps
    // by however much the copy above the selector grew or shrank.
    const anchorBefore = current.querySelector(ANCHOR_SELECTOR);
    const offsetBefore = anchorBefore ? anchorBefore.getBoundingClientRect().top : null;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`${url} returned ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextMain = doc.querySelector(MAIN_SELECTOR);
      if (!nextMain) throw new Error(`no ${MAIN_SELECTOR} in the fetched page`);

      const incoming = new Map();
      nextMain.querySelectorAll(SECTION_SELECTOR).forEach((element) => {
        const key = sectionKey(element);
        if (key) incoming.set(key, element);
      });

      // The main product section goes LAST. `floating-cart-bar` is rendered
      // inside it and resolves its reveal trigger (`.accreditations`) once, in
      // connectedCallback. Swapped in first, it would resolve against the old
      // accreditations node, which is then detached — leaving an
      // IntersectionObserver watching a node that is no longer in the document,
      // so the sticky bar would never appear again after a size switch.
      const productSection = current.closest(SECTION_SELECTOR);
      const sections = Array.from(main.querySelectorAll(SECTION_SELECTOR));
      const ordered = sections
        .filter((element) => element !== productSection)
        .concat(productSection ? [productSection] : []);

      const swapped = [];
      ordered.forEach((element) => {
        const key = sectionKey(element);
        const next = key && incoming.get(key);
        if (!next) return;
        element.replaceWith(next);
        swapped.push(next);
      });
      if (swapped.length === 0) throw new Error('no matching sections to swap');

      // Sections arriving this way were never seen by the observer set up on
      // DOMContentLoaded, so anything below the fold would animate in only if
      // it is re-registered. Called directly rather than by dispatching
      // `shopify:section:load`, because that listener passes
      // isDesignModeEvent = true, which suppresses the animation instead of
      // arming it.
      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        swapped.forEach((element) => window.initializeScrollAnimationTrigger(element));
      }

      if (offsetBefore !== null) {
        const anchorAfter = document.querySelector(ANCHOR_SELECTOR);
        if (anchorAfter) window.scrollBy(0, anchorAfter.getBoundingClientRect().top - offsetBefore);
      }

      if (doc.title) document.title = doc.title;
      if (push) history.pushState({ trueRestSize: url }, '', url);
      return true;
    } catch (error) {
      if (error.name === 'AbortError') return true;
      console.warn('[size-switcher] falling back to navigation:', error);
      return false;
    } finally {
      if (inFlight === controller) inFlight = null;
      const active = getProductInfo();
      if (active) active.classList.remove('product-info--switching');
    }
  }

  document.addEventListener('click', (event) => {
    // Let the browser handle open-in-new-tab, middle-click and the like.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest(PILL_SELECTOR);
    if (!link || !link.href) return;

    const url = new URL(link.href);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    swapTo(url.pathname, { push: true }).then((ok) => {
      if (!ok) window.location.href = link.href;
    });
  });

  // The history entry the page was LOADED on carries no state of ours, so
  // going back to it used to leave the previous size's content on screen under
  // the previous size's URL — the handler below bailed out and, because the
  // switch was a pushState rather than a navigation, the browser had nothing to
  // reload. Stamping the entry on init gives back/forward something to match.
  if (!history.state || !history.state.trueRestSize) {
    history.replaceState({ trueRestSize: window.location.pathname }, '', window.location.href);
  }

  // Back/forward between sizes should behave like the navigation it replaced.
  window.addEventListener('popstate', (event) => {
    if (!event.state || !event.state.trueRestSize) return;
    swapTo(window.location.pathname, { push: false }).then((ok) => {
      if (!ok) window.location.reload();
    });
  });
})();
